import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  ArrowLeft, ArrowUpDown, Loader2, Download, Send, Mail, Lock, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR');
};

const COLUMNS = [
  { key: 'name', label: 'Nom complet', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'phone', label: 'Téléphone', type: 'text' },
  { key: 'commune', label: 'Commune', type: 'text' },
  { key: 'formule', label: 'Formule', type: 'text' },
  { key: 'fondatrice', label: 'Fondatrice', type: 'bool' },
  { key: 'tarif', label: 'Tarif appliqué', type: 'num' },
  { key: 'created_at', label: 'Date de préinscription', type: 'date' },
  { key: 'confirmed', label: 'Statut', type: 'status' },
  { key: 'derniere_relance', label: 'Dernière relance', type: 'date' },
  { key: 'nombre_relances', label: 'Nb relances', type: 'num' },
];

const cellDisplay = (row, col) => {
  const v = row[col.key];
  switch (col.type) {
    case 'bool': return v ? 'Oui' : 'Non';
    case 'status': return v ? 'Confirmée' : 'Non confirmée';
    case 'date': return fmtDate(v);
    case 'num': return v === null || v === undefined ? '—' : (col.key === 'tarif' ? `${v} €` : v);
    default: return v || '—';
  }
};

const cellSortValue = (row, col) => {
  const v = row[col.key];
  switch (col.type) {
    case 'bool': return v ? 1 : 0;
    case 'status': return v ? 1 : 0;
    case 'date': return v ? new Date(v).getTime() : 0;
    case 'num': return v || 0;
    default: return (v || '').toString().toLowerCase();
  }
};

const cellFilterText = (row, col) => cellDisplay(row, col).toString().toLowerCase();

export default function ClientsDrilldownPanel({ segment, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmRelance, setConfirmRelance] = useState(null); // {emails, label}
  const [sending, setSending] = useState(false);

  const params = useMemo(() => ({
    status: segment.status || 'all',
    plan: segment.plan || 'all',
    pricing: segment.pricing || 'all',
    founder: segment.founder || 'all',
  }), [segment]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats/clients`, { params, withCredentials: true });
      setData(res.data);
      setSelected(new Set());
    } catch (e) {
      toast.error('Impossible de charger la liste des clientes.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    let out = [...data.rows];
    // filtres par colonne
    Object.entries(filters).forEach(([key, text]) => {
      if (!text) return;
      const col = COLUMNS.find((c) => c.key === key);
      const t = text.toLowerCase();
      out = out.filter((r) => cellFilterText(r, col).includes(t));
    });
    // tri
    const col = COLUMNS.find((c) => c.key === sortKey);
    if (col) {
      out.sort((a, b) => {
        const va = cellSortValue(a, col);
        const vb = cellSortValue(b, col);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [data, filters, sortKey, sortDir]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.email));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.email)));
  };
  const toggleOne = (email) => {
    const next = new Set(selected);
    next.has(email) ? next.delete(email) : next.add(email);
    setSelected(next);
  };

  const openDetail = async (email) => {
    setDetailLoading(true);
    setDetail({ email });
    try {
      const res = await axios.get(`${API}/admin/stats/client-detail`, { params: { email }, withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error('Fiche cliente indisponible.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const doRelance = async () => {
    if (!confirmRelance) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/stats/relance`,
        { emails: confirmRelance.emails }, { withCredentials: true });
      const d = res.data;
      if (!d.client_emails_enabled) {
        toast.warning(`Mode préparation (R1) : aucun email envoyé. ${d.blocked} tentative(s) journalisée(s).`, { duration: 7000 });
      } else {
        toast.success(`Relances : ${d.sent} envoyée(s), ${d.blocked} bloquée(s), ${d.skipped} ignorée(s).`);
      }
      setConfirmRelance(null);
      load();
    } catch (e) {
      toast.error('Échec de la relance.');
    } finally {
      setSending(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats/export-clients-csv`, { params, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PURO_clientes_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé.');
    } catch (e) {
      toast.error('Export CSV impossible.');
    }
  };

  const totals = data?.totals;

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="clients-drilldown-panel">
      <CardContent className="p-6 space-y-5">
        {/* En-tête + Retour */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={onBack}
              className="rounded-full border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white"
              data-testid="drilldown-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux statistiques
            </Button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Liste des clientes</div>
              <h3 className="font-heading text-lg text-[#493329]" data-testid="drilldown-title">{segment.label || 'Segment'}</h3>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadCsv}
              className="rounded-full border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white"
              data-testid="drilldown-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button size="sm" disabled={selected.size === 0}
              onClick={() => setConfirmRelance({ emails: [...selected], label: `${selected.size} cliente(s) sélectionnée(s)` })}
              className="rounded-full bg-[#C76542] hover:bg-[#b2573a] text-white disabled:opacity-40"
              data-testid="drilldown-group-relance-btn">
              <Send className="w-4 h-4 mr-1" /> Relancer la sélection ({selected.size})
            </Button>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
          </div>
        ) : (
          <>
            {/* Tableau */}
            <div className="overflow-x-auto rounded-xl border border-[#E9DDCC]">
              <table className="w-full text-xs" data-testid="drilldown-table">
                <thead className="bg-[#493329] text-white">
                  <tr>
                    <th className="px-2 py-2">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll}
                        aria-label="Tout sélectionner" data-testid="drilldown-select-all" />
                    </th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className="px-2 py-2 font-medium whitespace-nowrap text-left">
                        <button onClick={() => toggleSort(c.key)}
                          className="inline-flex items-center gap-1 hover:text-[#E9DDCC]"
                          data-testid={`sort-${c.key}`}>
                          {c.label}
                          <ArrowUpDown className={`w-3 h-3 ${sortKey === c.key ? 'text-[#C99A55]' : 'opacity-40'}`} />
                        </button>
                      </th>
                    ))}
                    <th className="px-2 py-2 font-medium">Action</th>
                  </tr>
                  {/* Ligne de filtres */}
                  <tr className="bg-[#5c4535]">
                    <th />
                    {COLUMNS.map((c) => (
                      <th key={c.key} className="px-2 py-1.5">
                        <input
                          value={filters[c.key] || ''}
                          onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })}
                          placeholder="Filtrer…"
                          className="w-full h-7 rounded border border-[#7a614e] bg-white/95 px-1.5 text-[11px] text-[#493329] placeholder:text-[#AE9885] focus:outline-none"
                          data-testid={`filter-${c.key}`}
                        />
                      </th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EFE4D0]">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-[#FAF6EF]" data-testid={`client-row-${r.email}`}>
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" checked={selected.has(r.email)} onChange={() => toggleOne(r.email)}
                          aria-label={`Sélectionner ${r.email}`} data-testid={`select-${r.email}`} />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <button onClick={() => openDetail(r.email)}
                          className="font-medium text-[#9A744C] underline-offset-2 hover:underline"
                          data-testid={`client-name-${r.email}`}>
                          {r.name || '—'}
                        </button>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">{r.email}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{r.phone || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{r.commune || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{r.formule}</td>
                      <td className="px-2 py-2 text-center">
                        {r.fondatrice
                          ? <Badge className="bg-[#C99A55] text-white text-[10px]">Oui</Badge>
                          : <span className="text-[#AE9885]">Non</span>}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">{r.tarif != null ? `${r.tarif} €` : '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-2 py-2 text-center">
                        {r.confirmed
                          ? <span className="text-emerald-700">Confirmée</span>
                          : <span className="text-[#C76542]">Non confirmée</span>}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">{fmtDate(r.derniere_relance)}</td>
                      <td className="px-2 py-2 text-center">{r.nombre_relances}</td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => setConfirmRelance({ emails: [r.email], label: r.name || r.email })}
                          className="inline-flex items-center gap-1 text-[#C76542] hover:text-[#b2573a]"
                          data-testid={`relance-${r.email}`}>
                          <Mail className="w-3.5 h-3.5" /> Relancer
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={COLUMNS.length + 2} className="px-3 py-6 text-center text-[#AE9885]" data-testid="drilldown-empty">
                      Aucune cliente dans ce segment.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totaux */}
            <div className="grid md:grid-cols-4 gap-3" data-testid="drilldown-totals">
              <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Clientes affichées</div>
                <div className="font-heading text-2xl text-[#493329]" data-testid="total-count">{rows.length}<span className="text-sm text-[#AE9885]"> / {totals.count}</span></div>
              </div>
              <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#C99A55]/40">
                <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">CA mensuel TTC</div>
                <div className="font-heading text-2xl text-[#C99A55]" data-testid="total-ca-ttc">{eur(totals.ca_mensuel_ttc)}</div>
              </div>
              <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">CA mensuel HT (TVA {Math.round(totals.tva_rate * 100)}%)</div>
                <div className="font-heading text-2xl text-[#493329]" data-testid="total-ca-ht">{eur(totals.ca_mensuel_ht)}</div>
              </div>
              <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Répartition par formule</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {totals.by_formule.map((f) => (
                    <span key={f.formule} className="text-[11px] bg-white border border-[#E9DDCC] rounded-full px-2 py-0.5 text-[#493329]">
                      {f.formule} : <strong>{f.count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Dialog confirmation relance (R1) */}
      <Dialog open={!!confirmRelance} onOpenChange={(o) => !o && setConfirmRelance(null)}>
        <DialogContent className="max-w-md" data-testid="relance-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#493329]">Confirmer la relance</DialogTitle>
            <DialogDescription>
              Vous allez relancer <strong>{confirmRelance?.emails?.length}</strong> cliente(s) : {confirmRelance?.label}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-3 text-sm text-[#493329]">
            <Lock className="w-4 h-4 mt-0.5 text-[#9A744C] shrink-0" />
            <span>
              <strong>Règle R1 active.</strong> Tant que l'interrupteur « Emails clientes » n'est pas activé,
              aucun email ne part réellement : les tentatives sont seulement journalisées.
            </span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRelance(null)} data-testid="relance-cancel-btn">Annuler</Button>
            <Button onClick={doRelance} disabled={sending}
              className="bg-[#C76542] hover:bg-[#b2573a] text-white" data-testid="relance-confirm-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              OK pour envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog fiche détaillée */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#493329]">{detail?.name || 'Fiche cliente'}</DialogTitle>
            <DialogDescription>{detail?.email}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div>
          ) : detail && detail.id ? (
            <div className="space-y-4 text-sm text-[#493329]">
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4">
                <div className="text-[#7A6455]">Téléphone</div><div>{detail.phone || '—'}</div>
                <div className="text-[#7A6455]">Commune</div><div>{detail.commune || '—'}</div>
                <div className="text-[#7A6455]">Formule</div><div>{detail.formule}</div>
                <div className="text-[#7A6455]">Fondatrice</div><div>{detail.fondatrice ? 'Oui' : 'Non'}</div>
                <div className="text-[#7A6455]">Tarif appliqué</div><div>{detail.tarif != null ? `${detail.tarif} €` : '—'}</div>
                <div className="text-[#7A6455]">Statut</div><div>{detail.confirmed ? 'Confirmée' : 'Non confirmée'}</div>
                <div className="text-[#7A6455]">Préinscription</div><div>{fmtDate(detail.created_at)}</div>
                <div className="text-[#7A6455]">Source</div><div>{detail.signup_source || '—'}</div>
                <div className="text-[#7A6455]">Nb relances</div><div>{detail.nombre_relances}</div>
                <div className="text-[#7A6455]">Dernière relance</div><div>{fmtDate(detail.derniere_relance)}</div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1.5">Consentements</div>
                {detail.consents?.length ? (
                  <ul className="space-y-1">
                    {detail.consents.map((c, i) => (
                      <li key={i} className="flex justify-between border-b border-[#EFE4D0] py-1">
                        <span>{c.type}{c.cgv_version ? ` (v${c.cgv_version})` : ''}</span>
                        <span className={c.accepted ? 'text-emerald-700' : 'text-[#C76542]'}>
                          {c.accepted ? 'Accepté' : 'Refusé'} · {fmtDate(c.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-[#AE9885]">Aucun consentement enregistré.</p>}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1.5">Pré-réservations</div>
                {detail.reservations?.length ? (
                  <ul className="space-y-1">
                    {detail.reservations.map((r, i) => (
                      <li key={i} className="flex justify-between border-b border-[#EFE4D0] py-1">
                        <span>{[r.day, r.time].filter(Boolean).join(' ') || r.slot_id}</span>
                        <span className="text-[#7A6455]">{r.status} · {fmtDate(r.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-[#AE9885]">Aucune pré-réservation.</p>}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1.5">Historique des relances</div>
                {detail.relances?.length ? (
                  <ul className="space-y-1">
                    {detail.relances.map((r, i) => (
                      <li key={i} className="flex justify-between border-b border-[#EFE4D0] py-1">
                        <span>{r.type} · {r.channel}</span>
                        <span className="text-[#7A6455]">{r.status} · {fmtDate(r.sent_at)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-[#AE9885]">Aucune relance enregistrée (historique détaillé).</p>}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDetail(null)}><X className="w-4 h-4 mr-1" />Fermer</Button>
                <Button onClick={() => { setConfirmRelance({ emails: [detail.email], label: detail.name || detail.email }); }}
                  className="bg-[#C76542] hover:bg-[#b2573a] text-white" data-testid="detail-relance-btn">
                  <Mail className="w-4 h-4 mr-1" /> Relancer
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
